<script setup>
import { ref, computed } from "vue";
import { useConsentStatus } from "@/composables/useConsentStatus";
import { AuthService } from "@/services/authService";
import EmailConfirmationModal from "../EmailConfirmationModal.vue";

const { isFormDisabled, hasConsent, testMode } = useConsentStatus();

const props = defineProps({
  surveyData: {
    type: Object,
    required: true,
  },
  currentStage: Number,
  totalStages: Number,
  nextStageInfo: Object,
});

const emit = defineEmits(["next-stage", "skip-stage", "user-authenticated"]);

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const isLoading = ref(false);
const message = ref("");
const messageType = ref(""); // "success" or "error"
const isAuthenticated = ref(false);
const loginLinkSent = ref(false);
const showEmailModal = ref(false);

const isEmailValid = computed(() => {
  const email = props.surveyData.email;
  return !!(email && email.trim() !== "" && emailRegex.test(email.trim()));
});

const isFormValid = computed(() => {
  const firstName = props.surveyData.firstName?.trim();
  const lastName = props.surveyData.lastName?.trim();
  const email = props.surveyData.email?.trim();

  return Boolean(firstName && lastName && email && isEmailValid.value);
});

const showMessage = (text, type) => {
  message.value = text;
  messageType.value = type;
};

const handleEmailAuth = async () => {
  if (!isFormValid.value) {
    return;
  }

  isLoading.value = true;
  message.value = "";
  messageType.value = "";

  try {
    // Additional validation before proceeding
    if (!props.surveyData.email || props.surveyData.email.trim() === "") {
      showMessage(
        "Email address is required. Please enter your email address.",
        "error"
      );
      return;
    }

    // Trim the email to ensure consistency
    const trimmedEmail = props.surveyData.email.trim();
    props.surveyData.email = trimmedEmail;

    // Validate email format
    if (!emailRegex.test(trimmedEmail)) {
      showMessage("Please enter a valid email address.", "error");
      return;
    }

    // Check if user exists
    const userExists = await AuthService.checkUserExists(trimmedEmail);

    if (userExists) {
      // User exists - send login link
      const result = await AuthService.sendLoginLink(
        trimmedEmail,
        props.surveyData.firstName,
        props.surveyData.lastName
      );

      if (result.success) {
        showMessage(
          "Login link sent to your email! Please check your inbox and click the link to continue.",
          "success"
        );
        loginLinkSent.value = true;
      } else {
        showMessage(result.message, "error");
      }
    } else {
      // User doesn't exist - create account and automatically sign them in
      const createResult = await AuthService.createAccount(
        trimmedEmail,
        props.surveyData.firstName,
        props.surveyData.lastName
      );

      if (createResult.success) {
        // User is now automatically signed in after account creation
        showMessage(
          "Account created successfully! You are now signed in and can continue with the survey.",
          "success"
        );
        isAuthenticated.value = true;

        // Emit authentication event to parent
        emit("user-authenticated");

        // Automatically proceed to next stage after a short delay
        setTimeout(() => {
          emit("next-stage");
        }, 2000);
      } else {
        // If account creation failed due to email already in use, try sending login link
        if (createResult.message.includes("already exists")) {
          const linkResult = await AuthService.sendLoginLink(
            trimmedEmail,
            props.surveyData.firstName,
            props.surveyData.lastName
          );

          if (linkResult.success) {
            showMessage(
              "Login link sent to your email! Please check your inbox and click the link to continue.",
              "success"
            );
            loginLinkSent.value = true;
          } else {
            showMessage(linkResult.message, "error");
          }
        } else {
          showMessage(createResult.message, "error");
        }
      }
    }
  } catch (error) {
    showMessage(
      "An error occurred during authentication. Please try again.",
      "error"
    );
  } finally {
    isLoading.value = false;
  }
};

const handleNext = () => {
  if (testMode.value) {
    emit("next-stage");
    return;
  }
  if (isAuthenticated.value) {
    emit("next-stage");
  } else if (loginLinkSent.value) {
    // If login link was sent but user is not yet authenticated, show message
    showMessage(
      "Please check your email and click the login link to continue.",
      "error"
    );
  } else {
    handleEmailAuth();
  }
};

// Check for email link on component mount
// onMounted(async () => {
//   // Email link handling is now done at the app level in main.js
//   // to prevent router guard race conditions
// });

// Listen for auth state changes
AuthService.onAuthStateChanged(async (user) => {
  if (user) {
    isAuthenticated.value = true;

    // Emit authentication event to parent
    emit("user-authenticated");

    // If user is authenticated and we're on contact-info, auto-proceed
    if (user.email) {
      props.surveyData.email = user.email;

      // Load user data and auto-proceed
      const userData = await AuthService.getUserData(user.uid);
      if (userData && userData.surveyData) {
        Object.assign(props.surveyData, userData.surveyData);
      }

      // Auto-proceed to next stage
      setTimeout(() => {
        emit("next-stage");
      }, 1000);
    }
  } else {
    isAuthenticated.value = false;
  }
});

const handleSkipStage = () => {
  emit("skip-stage");
};

// Email confirmation callback for modal
const getEmailFromModal = () => {
  return new Promise((resolve) => {
    showEmailModal.value = true;

    const handleConfirm = (confirmedEmail) => {
      showEmailModal.value = false;
      resolve(confirmedEmail);
    };

    const handleCancel = () => {
      showEmailModal.value = false;
      resolve(null);
    };

    // Store handlers temporarily
    window.__emailModalHandlers = { handleConfirm, handleCancel };
  });
};

// Handle modal events
const handleEmailConfirm = (confirmedEmail) => {
  if (window.__emailModalHandlers) {
    window.__emailModalHandlers.handleConfirm(confirmedEmail);
    delete window.__emailModalHandlers;
  }
};

const handleEmailCancel = () => {
  if (window.__emailModalHandlers) {
    window.__emailModalHandlers.handleCancel();
    delete window.__emailModalHandlers;
  }
};

const handleResendLink = async () => {
  if (!props.surveyData.email) {
    showMessage("Please enter your email address first.", "error");
    return;
  }

  isLoading.value = true;
  message.value = "";
  messageType.value = "";

  try {
    const result = await AuthService.sendLoginLink(
      props.surveyData.email.trim(),
      props.surveyData.firstName,
      props.surveyData.lastName
    );

    if (result.success) {
      showMessage(
        "Login link resent to your email! Please check your inbox.",
        "success"
      );
    } else {
      showMessage(result.message, "error");
    }
  } catch (error) {
    console.error("Error resending login link:", error);
    showMessage(
      "An error occurred while resending the login link. Please try again.",
      "error"
    );
  } finally {
    isLoading.value = false;
  }
};
</script>

<template>
  <div class="survey-form">
    <img
      src="@/assets/cyberireland_logo.png"
      alt="Cyber Ireland Logo"
      class="logo"
      style="width: 300px; margin: 0 auto; display: block"
    />
    <h2>Contact Information</h2>

    <div class="intro-text">
      <p>
        Please provide your contact information to begin the survey.
        <strong>New users</strong> will have an account created and be
        automatically signed in. <strong>Existing users</strong> will receive a
        secure login link to their email address.
      </p>
    </div>

    <!-- First Name -->
    <div class="form-group">
      <label for="firstName">First Name</label>
      <input
        type="text"
        id="firstName"
        v-model="surveyData.firstName"
        placeholder="Enter your first name"
        required
        :disabled="isFormDisabled"
      />
    </div>

    <!-- Last Name -->
    <div class="form-group">
      <label for="lastName">Last Name</label>
      <input
        type="text"
        id="lastName"
        v-model="surveyData.lastName"
        placeholder="Enter your last name"
        required
        :disabled="isFormDisabled"
      />
    </div>

    <!-- Email -->
    <div class="form-group">
      <label for="email">Email Address</label>
      <input
        type="email"
        id="email"
        v-model="surveyData.email"
        placeholder="Enter your email address"
        required
        :disabled="isFormDisabled"
      />
      <small v-if="surveyData.email && !isEmailValid" class="error-message">
        Please enter a valid email address
      </small>
    </div>

    <!-- Message Display -->
    <div v-if="message" :class="`message ${messageType}`">
      {{ message }}
    </div>

    <div class="button-group" v-if="!loginLinkSent && !isAuthenticated">
      <button
        @click="handleNext"
        class="primary-button"
        :disabled="!isFormValid || isLoading || isFormDisabled"
      >
        <span v-if="isLoading">Processing...</span>
        <span v-else-if="isFormDisabled">Consent Required</span>
        <span v-else>Create account and continue</span>
      </button>
    </div>

    <div v-if="loginLinkSent && !isAuthenticated" class="button-group">
      <button @click="handleNext" class="primary-button" :disabled="true">
        <span>Please check your email and click the login link</span>
      </button>
      <button
        @click="handleResendLink"
        class="secondary-button"
        :disabled="isLoading"
      >
        <span v-if="isLoading">Resending...</span>
        <span v-else>Resend login link</span>
      </button>
    </div>

    <div v-if="isAuthenticated" class="auth-success">
      <p>
        ✅ You're successfully logged in! You can now continue with the survey.
      </p>
      <button @click="handleNext" class="primary-button">
        Continue Survey
      </button>
    </div>

    <!-- Email Confirmation Modal -->
    <EmailConfirmationModal
      :show="showEmailModal"
      @confirm="handleEmailConfirm"
      @cancel="handleEmailCancel"
    />
  </div>
</template>

<style scoped>
.intro-text {
  background-color: var(--surface-color);
  padding: 1rem;
  border-radius: 8px;
  margin-bottom: 1.5rem;
  font-size: 0.95rem;
  line-height: 1.5;
  color: var(--text-color);
}

.form-group {
  margin-bottom: 1.5rem;
}

.form-group label {
  display: block;
  font-weight: 500;
  color: var(--text-color);
  font-size: 1rem;
}

.form-group input {
  width: 100%;
  padding: 0.875rem;
  border: 1px solid var(--border-color);
  border-radius: 8px;
  font-size: 1rem;
  transition: border-color 0.3s ease, box-shadow 0.3s ease;
  min-height: 48px; /* Touch-friendly height */
  background-color: white;
}

.form-group input:focus {
  outline: none;
  border-color: var(--primary-color);
  box-shadow: 0 0 0 2px rgba(66, 184, 131, 0.2);
}

.form-group input:disabled {
  background-color: #f5f5f5;
  cursor: not-allowed;
  opacity: 0.7;
}

.error-message {
  color: #dc3545;
  font-size: 0.875rem;
  margin-top: 0.5rem;
  display: block;
}

.message {
  padding: 0.875rem;
  border-radius: 8px;
  margin-bottom: 1.5rem;
  font-size: 0.9rem;
  line-height: 1.4;
}

.message.success {
  background-color: #d4edda;
  color: #155724;
  border: 1px solid #c3e6cb;
}

.message.error {
  background-color: #f8d7da;
  color: #721c24;
  border: 1px solid #f5c6cb;
}

.button-group {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-top: 2rem;
}

.primary-button {
  background-color: var(--primary-color);
  color: white;
  padding: 0.875rem 1.5rem;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 48px; /* Touch-friendly height */
  width: 100%;
}

.primary-button:hover:not(:disabled) {
  background-color: var(--primary-hover);
}

.primary-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.secondary-button {
  background-color: transparent;
  color: var(--primary-color);
  border: 1px solid var(--primary-color);
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-top: 0.5rem;
}

.secondary-button:hover:not(:disabled) {
  background-color: var(--primary-color);
  color: white;
}

.secondary-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.auth-instructions {
  margin-top: 0.5rem;
  font-size: 0.9rem;
  color: var(--text-color);
  background-color: #fff3cd;
  padding: 0.75rem;
  border-radius: 6px;
  border: 1px solid #ffeaa7;
  line-height: 1.4;
}

.auth-success {
  background-color: #d4edda;
  color: #155724;
  padding: 1rem;
  border-radius: 8px;
  margin-top: 1rem;
  border: 1px solid #c3e6cb;
}

.auth-success p {
  margin: 0 0 1rem 0;
  font-weight: 500;
}

/* Mobile-specific improvements */
@media (max-width: 768px) {
  .intro-text {
    padding: 0.875rem;
    font-size: 0.9rem;
    margin-bottom: 1.25rem;
  }

  .form-group {
    margin-bottom: 1.25rem;
  }

  .form-group label {
    font-size: 0.95rem;
  }

  .form-group input {
    padding: 0.75rem;
    font-size: 0.95rem;
    min-height: 44px;
  }

  .error-message {
    font-size: 0.8rem;
    margin-top: 0.375rem;
  }

  .message {
    padding: 0.75rem;
    font-size: 0.85rem;
    margin-bottom: 1.25rem;
  }

  .button-group {
    margin-top: 1.5rem;
    gap: 0.75rem;
  }

  .primary-button {
    padding: 0.75rem 1.25rem;
    font-size: 0.95rem;
    min-height: 44px;
  }

  .auth-success {
    padding: 0.875rem;
    font-size: 0.9rem;
    margin-top: 1.25rem;
  }
}

@media (max-width: 480px) {
  .intro-text {
    padding: 0.75rem;
    font-size: 0.85rem;
    margin-bottom: 1rem;
  }

  .form-group {
    margin-bottom: 1rem;
  }

  .form-group label {
    font-size: 0.9rem;
  }

  .form-group input {
    padding: 0.625rem;
    font-size: 0.9rem;
    min-height: 40px;
  }

  .error-message {
    font-size: 0.75rem;
    margin-top: 0.25rem;
  }

  .message {
    padding: 0.625rem;
    font-size: 0.8rem;
    margin-bottom: 1rem;
  }

  .button-group {
    margin-top: 1.25rem;
    gap: 0.625rem;
  }

  .primary-button {
    padding: 0.625rem 1rem;
    font-size: 0.9rem;
    min-height: 40px;
  }

  .auth-success {
    padding: 0.75rem;
    font-size: 0.85rem;
    margin-top: 1rem;
  }
}
</style>
